// pages/AIChatHistory.js - Browser riwayat AI chat (admin & user)
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { MarkdownRenderer } from '../components/AI/MarkdownRenderer';
import { DataTable } from '../components/AI/AIChat';
import * as XLSX from 'xlsx';
import {
  MessageSquare, User as UserIcon, Bot, Search, Trash2, RefreshCw,
  Calendar, Zap, Clock, Target, Database, ChevronRight,
  AlertCircle, BarChart3, Activity, Loader2, X, Download, PlayCircle, ClipboardCheck,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { toChatMessages } from '../components/AI/sessionResume';
import ImageLightbox from '../components/AI/ImageLightbox';
import ToolTrace from '../components/AI/ToolTrace';
import AIReviewFindings from '../components/AI/AIReviewFindings';

// Tab yang boleh datang dari `?tab=` (QuickSearch).
const AIHIST_TABS = ['sessions', 'usage', 'analytics', 'forecast', 'review'];

function exportStamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function fmtDateTime(s, lang) {
  if (!s) return '-';
  // 'YYYY-MM-DD HH:mm:ss' (UTC dari SQLite datetime('now')) → local
  const d = new Date(s.replace(' ', 'T') + 'Z');
  return isNaN(d) ? s : d.toLocaleString(lang === 'en' ? 'en-US' : 'id-ID', { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtRelative(s, t, lang) {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + 'Z');
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)    return t('aih.relSec',  { n: Math.round(diff) });
  if (diff < 3600)  return t('aih.relMin',  { n: Math.round(diff/60) });
  if (diff < 86400) return t('aih.relHour', { n: Math.round(diff/3600) });
  if (diff < 86400*30) return t('aih.relDay', { n: Math.round(diff/86400) });
  return fmtDateTime(s, lang);
}

// ── [RESUME] Sesi Coding → payload untuk AIChat ───────────────────────────────
// Hanya sesi Coding yang bisa dilanjutkan. Sesi dashboard lain bergantung pada
// text-to-SQL + skema halaman yang aktif saat itu; memulihkannya butuh lebih
// dari transkrip, dan sesi yang "dilanjutkan" ke halaman yang salah akan
// menjawab dari sumber data yang berbeda tanpa memberi tahu siapa pun.
//
// Penanda dashboard = 'Coding' berasal dari activeLabel yang dikirim AICoding.js
// (DASH_HINTS tidak punya entri '__code__', jadi nama itu yang tersimpan).
const isCodingSession = (detail) =>
  String(detail?.meta?.dashboard || '').trim().toLowerCase() === 'coding';

// Bentuk pesan di DB riwayat (message_role/content/data) → bentuk yang dipakai
// state AIChat. Field token & timing sengaja TIDAK dibawa: badge-nya menjelaskan
// biaya giliran yang baru saja terjadi, dan menampilkannya kembali pada pesan
// lama membuat total di layar terbaca seperti biaya sesi yang sedang berjalan.
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon style={{ width: 20, height: 20 }} className="text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-slate-400 font-medium">{label}</div>
        <div className="text-lg font-bold text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}

export default function AIChatHistory() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const isAdmin  = user?.role === 'admin';

  const [sessions, setSessions]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [filterUser, setFilterUser] = useState('');
  // Toggle "hanya dari cache": menyaring daftar ke sesi yang punya minimal satu
  // jawaban dari semantic cache (kolom ai_chat_history.cached).
  const [cachedOnly, setCachedOnly] = useState(false);
  const [activeId, setActiveId]   = useState(null);
  const [detail, setDetail]       = useState(null);
  // Lightbox lampiran gambar. Di halaman ini state-nya ada di tingkat komponen
  // (bukan per-pesan seperti di AIChat) karena daftar pesannya dirender inline
  // dalam satu blok, tanpa komponen gelembung tersendiri.
  const [lightbox, setLightbox]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats]         = useState(null);
  const [error, setError]         = useState(null);
  const [exporting, setExporting] = useState('');   // '' | 'xlsx' | 'json'

  // #7 Usage Report: tab aktif + data konsumsi harian.
  const [searchParams] = useSearchParams();
  const [view, setView]           = useState(() => {
    const p = searchParams.get('tab');
    return AIHIST_TABS.includes(p) ? p : 'sessions';
  }); // 'sessions' | 'usage' | 'analytics'
  useTabDeepLink('ai_history', (p) => AIHIST_TABS.includes(p), setView);
  const [usageDays, setUsageDays] = useState(30);
  const [usage, setUsage]         = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  // Analytics memakai rentang hari SENDIRI, tidak berbagi dengan Usage Report.
  // Alasannya beda tujuan: Usage Report dibaca untuk menagih bulan berjalan,
  // Analytics untuk mencari pola — dan mengganti rentang di satu tab lalu
  // menemukan tab lain ikut berubah adalah kejutan yang tidak perlu.
  const [anaDays, setAnaDays]     = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [anaLoading, setAnaLoading] = useState(false);
  const [fcDays, setFcDays]       = useState(30);
  const [forecast, setForecast]   = useState(null);
  const [fcLoading, setFcLoading] = useState(false);
  const isMobile = useIsMobile();

  const loadSessions = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { limit: 100 };
      if (search)      params.search   = search;
      if (filterUser)  params.username = filterUser;
      if (cachedOnly)  params.cachedOnly = '1';
      const res = await api.get('/api/ai/chat-history/sessions', { params });
      setSessions(res.data.sessions || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [search, filterUser, cachedOnly]);

  const loadStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/api/ai/chat-history/stats');
      setStats(res.data);
    } catch (e) {
      console.warn('[AIChatHistory] stats failed:', e.message);
    }
  }, [isAdmin]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Context menu (klik kanan di session) + deep-link ?session=<id> ──────────
  const [ctxMenu, setCtxMenu] = useState(null);   // { x, y, sessionId }
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [ctxMenu]);

  const sessionUrl = (sessionId) =>
    `${window.location.origin}${window.location.pathname}?session=${encodeURIComponent(sessionId)}`;

  // Mode standalone: URL punya ?session=<id> (hasil "Buka di tab baru") →
  // tampilkan HANYA isi percakapan session tsb, full width, tanpa list/stats.
  const [standalone] = useState(() => {
    try { return !!new URLSearchParams(window.location.search).get('session'); }
    catch (_) { return false; }
  });

  // Saat halaman dibuka dengan ?session=<id> (hasil "Open in new tab") →
  // langsung buka detail session tersebut.
  useEffect(() => {
    try {
      const sid = new URLSearchParams(window.location.search).get('session');
      if (sid) loadDetail(sid);
    } catch (_) {}
  }, []); // eslint-disable-line

  const loadUsage = useCallback(async () => {
    if (!isAdmin) return;
    setUsageLoading(true);
    try {
      const res = await api.get('/api/ai/chat-history/usage-daily', { params: { days: usageDays } });
      setUsage(res.data);
    } catch (e) {
      console.warn('[AIChatHistory] usage failed:', e.message);
    } finally {
      setUsageLoading(false);
    }
  }, [isAdmin, usageDays]);

  useEffect(() => { if (view === 'usage') loadUsage(); }, [view, loadUsage]);

  const loadAnalytics = useCallback(async () => {
    if (!isAdmin) return;
    setAnaLoading(true);
    try {
      const res = await api.get('/api/ai/chat-history/analytics', { params: { days: anaDays } });
      setAnalytics(res.data);
    } catch (e) {
      console.warn('[AIChatHistory] analytics failed:', e.message);
      setAnalytics(null);
    } finally {
      setAnaLoading(false);
    }
  }, [isAdmin, anaDays]);

  useEffect(() => { if (view === 'analytics') loadAnalytics(); }, [view, loadAnalytics]);

  const loadForecast = useCallback(async () => {
    if (!isAdmin) return;
    setFcLoading(true);
    try {
      const res = await api.get('/api/ai/chat-history/cost-forecast', { params: { days: fcDays } });
      setForecast(res.data);
    } catch (e) {
      console.warn('[AIChatHistory] forecast failed:', e.message);
      setForecast(null);
    } finally {
      setFcLoading(false);
    }
  }, [isAdmin, fcDays]);

  useEffect(() => { if (view === 'forecast') loadForecast(); }, [view, loadForecast]);

  const loadDetail = async (sessionId) => {
    setActiveId(sessionId);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.get(`/api/ai/chat-history/session/${sessionId}`);
      setDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleContinue = async (d) => {
    if (!d?.session_id) return;
    // Praperiksa konteks di AI Service. Konfirmasi hanya muncul kalau memang ada
    // yang perlu dikonfirmasi — melanjutkan sesi yang konteksnya masih utuh
    // tidak boleh menambah satu klik pun.
    try {
      const { data } = await api.get(`/api/ai/chat-history/session/${d.session_id}/context-check`);
      if (data?.ok === false) {
        const go = window.confirm(
          'AI Service tidak lagi menyimpan konteks percakapan ini, jadi model TIDAK akan'
          + ' mengingat isinya. Transkrip lama tetap ditampilkan sebagai rujukan, dan giliran'
          + ' baru menempel ke sesi yang sama.\n\nLanjutkan?');
        if (!go) return;
      } else if (data?.ok === null) {
        const go = window.confirm(
          'Status konteks tidak bisa diperiksa (AI Service tidak merespons).'
          + ' Lanjutkan saja?');
        if (!go) return;
      }
    } catch (_) {
      // Pemeriksaan gagal bukan alasan memblokir — paling buruk user menemukan
      // sendiri bahwa model tidak ingat, keadaan yang sama seperti sebelum
      // fitur ini ada.
    }
    // Sesi lama TIDAK dihapus dan TIDAK ditandai. Giliran baru menempel ke
    // session_id yang sama, jadi di halaman ini keduanya tetap tampil sebagai
    // satu sesi — itu memang keadaan yang benar.
    navigate('/dashboard/ai-coding', {
      state: { resume: { session_id: d.session_id, messages: toChatMessages(d, lang) } },
    });
  };

  const handleDelete = async (sessionId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(t('aih.confirmDelete'))) return;
    try {
      await api.delete(`/api/ai/chat-history/session/${sessionId}`);
      if (activeId === sessionId) { setActiveId(null); setDetail(null); }
      loadSessions(); loadStats();
    } catch (e) {
      alert(t('aih.deleteFailed', { err: e.response?.data?.error || e.message }));
    }
  };

  const handleExportAll = async () => {
    if (exporting) return;
    setExporting('xlsx');
    try {
      const params = {};
      if (search)     params.search   = search;      // ikut filter aktif
      if (filterUser) params.username = filterUser;
      const res = await api.get('/api/ai/chat-history/export', { params });
      const rows = res.data?.rows || [];
      if (!rows.length) { alert(t('aih.exportEmpty')); return; }

      // Header kolom Excel dibiarkan Inggris (konvensi export).
      const HEADERS = ['session_id', 'session_started_at', 'username', 'dashboard', 'provider', 'model',
        'message_role', 'content', 'tokens_prompt', 'tokens_completion', 'tokens_total',
        'response_time', 'accuracy', 'has_data', 'data_source', 'created_at'];
      const CAP = 32767; // batas panjang satu sel Excel
      const aoa = [HEADERS];
      for (const r of rows) {
        aoa.push(HEADERS.map(h => {
          let v = r[h];
          if (v === null || v === undefined) return '';
          if (typeof v === 'string' && v.length > CAP) v = v.slice(0, CAP - 1) + '…';
          return v;
        }));
      }
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'AI Chat');
      XLSX.writeFile(wb, `ai-chat-export-${exportStamp()}.xlsx`);
      if (res.data?.truncated) alert(t('aih.exportTruncated', { n: (res.data.max || rows.length).toLocaleString() }));
    } catch (e) {
      alert(t('aih.exportFailed', { err: e.response?.data?.error || e.message }));
    } finally {
      setExporting('');
    }
  };

  const handleExportJson = async () => {
    if (exporting) return;
    setExporting('json');
    try {
      const params = { full: 1 };   // sertakan `data` (hasil query per pesan) + content penuh
      if (search)     params.search   = search;
      if (filterUser) params.username = filterUser;
      const res = await api.get('/api/ai/chat-history/export', { params });
      const rows = res.data?.rows || [];
      if (!rows.length) { alert(t('aih.exportEmpty')); return; }

      const payload = {
        exported_at: new Date().toISOString(),
        count: rows.length,
        truncated: !!res.data?.truncated,
        filter: { search: search || null, username: filterUser || null },
        rows,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `ai-chat-export-${exportStamp()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      if (res.data?.truncated) alert(t('aih.exportTruncated', { n: (res.data.max || rows.length).toLocaleString() }));
    } catch (e) {
      alert(t('aih.exportFailed', { err: e.response?.data?.error || e.message }));
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Context menu klik-kanan session */}
      {ctxMenu && (
        <div
          style={{ position: 'fixed', top: Math.min(ctxMenu.y, window.innerHeight - 120), left: Math.min(ctxMenu.x, window.innerWidth - 230), zIndex: 2000 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 w-[215px] text-sm">
          <button
            onClick={() => { window.open(sessionUrl(ctxMenu.sessionId), '_blank', 'noopener'); setCtxMenu(null); }}
            className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2">
            <ChevronRight style={{ width: 13, height: 13 }} className="text-indigo-500" />
            {t('aiHist.openNewTab')}
          </button>
          <button
            onClick={() => {
              try { navigator.clipboard.writeText(sessionUrl(ctxMenu.sessionId)); } catch (_) {}
              setCtxMenu(null);
            }}
            className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2">
            <Download style={{ width: 13, height: 13 }} className="text-slate-400 rotate-180" />
            {t('aiHist.copySessionLink')}
          </button>
          <div className="my-1 border-t border-slate-100" />
          <button
            onClick={(e) => { handleDelete(ctxMenu.sessionId, e); setCtxMenu(null); }}
            className="w-full text-left px-3.5 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2">
            <Trash2 style={{ width: 13, height: 13 }} />
            {t('aiHist.deleteSession')}
          </button>
        </div>
      )}
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare style={{ width: 24, height: 24 }} className="text-indigo-500" />
            {t('aih.title')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? t('aih.subtitleAdmin')
              : t('aih.subtitleUser')}
          </p>
        </div>
        <button onClick={() => {
          loadSessions(); loadStats();
          if (view === 'usage')     loadUsage();
          if (view === 'analytics') loadAnalytics();
          if (view === 'forecast')  loadForecast();
        }}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">
          <RefreshCw style={{ width: 14, height: 14 }} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>
      {/* Tab switcher (admin only) — Sessions vs Usage Report */}
      {!standalone && isAdmin && (
        <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
          <button onClick={() => setView('sessions')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === 'sessions' ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t('aih.tabSessions')}
          </button>
          <button onClick={() => setView('usage')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              view === 'usage' ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <BarChart3 style={{ width: 14, height: 14 }} />
            {t('aih.tabUsage')}
          </button>
          {/* Label tab TIDAK lewat translations.js — sama alasannya dengan
              AIHistorySidebar: gerbang build check-i18n.js menuntut paritas
              id/en/ar untuk setiap key, dan "Analytics" adalah kata yang sama
              di ketiga bahasa UI yang dipakai di sini. */}
          <button onClick={() => setView('analytics')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              view === 'analytics' ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <Activity style={{ width: 14, height: 14 }} />
            Analytics
          </button>
          <button onClick={() => setView('forecast')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              view === 'forecast' ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <Target style={{ width: 14, height: 14 }} />
            Cost Forecast
          </button>
          {/* [REVIEW] Antrian jawaban bermasalah (👎 + scan otomatis). backend/aiReview.js */}
          <button onClick={() => setView('review')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              view === 'review' ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <ClipboardCheck style={{ width: 14, height: 14 }} />
            Review Findings
          </button>
        </div>
      )}
      {isAdmin && view === 'review' && <AIReviewFindings />}
      {/* ══ VIEW: COST FORECAST (admin) ══ */}
      {isAdmin && view === 'forecast' && (
        <CostForecast
          data={forecast} loading={fcLoading}
          days={fcDays} setDays={setFcDays} lang={lang}
        />
      )}
      {/* ══ VIEW: ANALYTICS (admin) ══ */}
      {isAdmin && view === 'analytics' && (
        <AnalyticsReport
          data={analytics} loading={anaLoading}
          days={anaDays} setDays={setAnaDays} lang={lang}
        />
      )}
      {/* ══ VIEW: USAGE REPORT (admin) ══ */}
      {isAdmin && view === 'usage' && (
        <UsageReport
          usage={usage} loading={usageLoading}
          days={usageDays} setDays={setUsageDays} t={t} lang={lang}
        />
      )}
      {/* ══ VIEW: SESSIONS (default) ══ */}
      {view === 'sessions' && (<>
      {/* Stats — admin only */}
      {!standalone && isAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard icon={MessageSquare} label={t("aih.statMessages")} value={stats.total.toLocaleString()} color="bg-indigo-500" />
          <StatCard icon={Activity}      label={t("aih.statSessions")}       value={stats.sessions.toLocaleString()} color="bg-brand-500" />
          <StatCard icon={UserIcon}      label={t("aih.statUsers")}          value={stats.users}                     color="bg-emerald-500" />
          <StatCard icon={Zap}           label={t("aih.statTokens")}   value={(stats.allTokens||0).toLocaleString()} color="bg-amber-500" />
          <StatCard icon={Calendar}      label={t("aih.statToday")}       value={stats.today}                     color="bg-rose-500" />
        </div>
      )}

      {/* Stats per provider (admin only) */}
      {!standalone && isAdmin && stats?.byProvider?.length > 0 && (
        <div className="mb-6 bg-white rounded-xl border border-slate-100 p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <BarChart3 style={{ width: 14, height: 14 }} />
            {t('aih.tokenUsageProvider')}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stats.byProvider.map(p => (
              <div key={p.provider} className="px-3 py-2 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500 font-medium">{p.provider}</div>
                <div className="text-sm font-bold text-slate-800 mt-0.5">
                  {(p.tokens||0).toLocaleString()} <span className="text-xs text-slate-400 font-normal">{t('ai.tokens')}</span>
                </div>
                <div className="text-xs text-slate-400">{t('aih.msgCount', { n: p.messages })}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      {!standalone && <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search style={{ width: 14, height: 14 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('aih.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
        </div>
        {isAdmin && (
          <input type="text" value={filterUser} onChange={e => setFilterUser(e.target.value)}
            placeholder={t('aih.filterUser')}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
        )}
        <button onClick={() => setCachedOnly(v => !v)}
          title={t('aih.cachedOnlyTitle')}
          aria-pressed={cachedOnly}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm transition-colors ${
            cachedOnly
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
          <span>⚡</span>{t('aih.cachedOnly')}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={handleExportAll} disabled={!!exporting || loading}
            title={t('aih.exportXlsx')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {exporting === 'xlsx' ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <Download style={{ width: 14, height: 14 }} />}
            {exporting === 'xlsx' ? t('aih.exporting') : t('aih.exportXlsx')}
          </button>
          <button onClick={handleExportJson} disabled={!!exporting || loading}
            title={t('aih.exportJson')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {exporting === 'json' ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <Download style={{ width: 14, height: 14 }} />}
            {exporting === 'json' ? t('aih.exporting') : t('aih.exportJson')}
          </button>
          <div className="text-xs text-slate-500">
            {t('aih.totalSessions', { n: total.toLocaleString() })}
          </div>
        </div>
      </div>}

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
          <AlertCircle style={{ width: 14, height: 14 }} />
          {error}
        </div>
      )}

      {/* Main grid: list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Sessions list — disembunyikan di mode standalone */}
        <div className={standalone ? 'hidden' : "lg:col-span-5 bg-white rounded-xl border border-slate-100 overflow-hidden"}>
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider">
            {t('aih.sessionsCount', { n: sessions.length })}
          </div>
          <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
            {loading && (
              <div className="p-8 text-center text-slate-400">
                <Loader2 style={{ width: 20, height: 20 }} className="animate-spin mx-auto mb-2" />
                {t('common.loading')}
              </div>
            )}
            {!loading && sessions.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">
                {t('aih.empty')}
              </div>
            )}
            {sessions.map(s => (
              <div key={s.session_id}
                onClick={() => loadDetail(s.session_id)}
                onContextMenu={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setCtxMenu({ x: e.clientX, y: e.clientY, sessionId: s.session_id });
                }}
                className={`px-4 py-3 border-b border-slate-50 cursor-pointer transition-all
                  ${activeId === s.session_id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : 'hover:bg-slate-50'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-bold uppercase">
                        {s.role || 'user'}
                      </span>
                      <span className="text-xs font-semibold text-slate-700 truncate">{s.username}</span>
                      <span className="text-xs text-slate-400 truncate">· {s.dashboard || '—'}</span>
                    </div>
                    <div className="text-sm text-slate-600 line-clamp-2 mb-1">
                      {s.first_question || <em className="text-slate-400">{t('aih.noQuestion')}</em>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <MessageSquare style={{ width: 10, height: 10 }} />
                        {s.message_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap style={{ width: 10, height: 10 }} />
                        {(s.total_tokens||0).toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock style={{ width: 10, height: 10 }} />
                        {fmtRelative(s.last_at, t, lang)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={(e) => handleDelete(s.session_id, e)}
                      title={t("aih.deleteSession")}
                      className="p-1 text-slate-300 hover:text-red-500 rounded">
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                    <ChevronRight style={{ width: 14, height: 14 }} className="text-slate-300" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail — mobile: overlay full-screen saat sesi dipilih */}
        <div className={isMobile
          ? (activeId
              ? 'fixed inset-0 z-[900] bg-white flex flex-col'
              : 'hidden')
          : `${standalone ? 'lg:col-span-12' : 'lg:col-span-7'} bg-white rounded-xl border border-slate-100 overflow-hidden flex flex-col`}>
          {!activeId && (
            <div className="flex-1 flex items-center justify-center p-12 text-center text-slate-400">
              <div>
                <MessageSquare style={{ width: 40, height: 40 }} className="mx-auto mb-3 text-slate-200" />
                <p className="text-sm">{t('aih.selectSession')}</p>
              </div>
            </div>
          )}
          {detailLoading && (
            <div className="flex-1 flex items-center justify-center p-12">
              <Loader2 style={{ width: 24, height: 24 }} className="animate-spin text-indigo-500" />
            </div>
          )}
          {detail && !detailLoading && (
            <>
              {/* Detail header */}
              <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-brand-50 to-brand-50 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 mb-0.5">
                    {t('aih.sessionLabel')} <code className="px-1 bg-white/60 rounded">{detail.session_id.slice(-12)}</code>
                  </div>
                  <div className="text-sm font-semibold text-slate-700 truncate">
                    {detail.meta?.username} · {detail.meta?.dashboard} · {detail.meta?.provider} / {detail.meta?.model}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isCodingSession(detail) && (
                    <button onClick={() => handleContinue(detail)}
                      title="Continue this session in AI Coding"
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                 bg-white/70 hover:bg-white text-brand-700 border border-brand-200">
                      <PlayCircle style={{ width: 14, height: 14 }} />
                      <span className="hidden sm:inline">Continue</span>
                    </button>
                  )}
                  <button onClick={() => { if (standalone) { window.close(); } else { setActiveId(null); setDetail(null); } }}
                    className="p-1.5 hover:bg-white/50 rounded-lg text-slate-400 hover:text-slate-600">
                    <X style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${isMobile ? '' : (standalone ? 'max-h-[calc(100vh-220px)]' : 'max-h-[calc(100vh-360px)]')}`}
                style={isMobile ? { paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' } : undefined}>
                {detail.messages.map(m => (
                  <div key={m.id} className={`flex gap-2 ${m.message_role === 'user' ? 'justify-end' : ''}`}>
                    {m.message_role !== 'user' && (
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5
                        ${m.message_role === 'error' ? 'bg-red-100' : 'bg-gradient-to-br from-brand-500 to-brand-700'}`}>
                        <Bot style={{ width: 14, height: 14 }} className={m.message_role === 'error' ? 'text-red-600' : 'text-white'} />
                      </div>
                    )}
                    <div className={`max-w-[85%] min-w-0 ${m.message_role === 'user' ? 'order-1' : ''}`}>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm
                        ${m.message_role === 'user'
                          ? 'bg-brand-600 text-white rounded-tr-sm'
                          : m.message_role === 'error'
                          ? 'bg-red-50 border border-red-200 text-red-700 rounded-tl-sm'
                          : 'bg-white border border-slate-100 text-slate-700 rounded-tl-sm shadow-sm'}`}>
                        {m.message_role === 'user' || m.message_role === 'error'
                          ? (
                            <>
                              {/* [THUMB] Lampiran gambar pertanyaan ini. Sumbernya
                                  data_json.images — miniatur, bukan gambar asli. */}
                              {Array.isArray(m.data?.images) && m.data.images.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {m.data.images.map((im, ii) => (
                                    im.thumb ? (
                                      <img key={ii} src={im.thumb} alt={im.name || 'image'}
                                        title={`${im.name || 'image'} — klik untuk perbesar`}
                                        onClick={() => setLightbox(im)}
                                        className="rounded-lg border border-white/30 object-cover cursor-zoom-in"
                                        style={{ width: 72, height: 72 }} />
                                    ) : (
                                      <span key={ii} title={im.name || ''}
                                        className="text-[11px] bg-white/20 rounded-md px-2 py-1 max-w-[140px] truncate">
                                        🖼 {im.name || 'image'}
                                      </span>
                                    )
                                  ))}
                                </div>
                              )}
                              <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                            </>
                          )
                          : <>
                              <MarkdownRenderer text={m.content} sources={m.data?.sources} />
                              {/* [TOOLS] Bukti tool yang dipanggil pada jawaban
                                  ini. Komponen yang sama dipakai transkrip hidup,
                                  jadi lencananya tidak mungkin berbeda antara
                                  keduanya. */}
                              <ToolTrace tools={m.data?.tools} compact
                                expected={!!m.data?.tools_expected} />
                              {Array.isArray(m.data?.rows) && m.data.rows.length > 0 && (
                                <DataTable rows={m.data.rows} />
                              )}
                            </>
                        }
                      </div>
                      <div className={`mt-1 flex items-center gap-2 text-xs text-slate-400 flex-wrap ${m.message_role === 'user' ? 'justify-end' : ''}`}>
                        <span>{fmtDateTime(m.created_at, lang)}</span>
                        {String(m.data_source || '').includes('semantic-cache') && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200 text-[10px] font-semibold"
                            title={t('aiHist.answerServedAiServiceSemantic')}>
                            ⚡ cache
                          </span>
                        )}
                        {m.tokens_total > 0 && (
                          <span title={m.tokens_estimated
                              ? `Estimasi (provider tidak mengirim usage) — Prompt: ${m.tokens_prompt} | Completion: ${m.tokens_completion}`
                              : `Token asli dari provider — Prompt: ${m.tokens_prompt} | Completion: ${m.tokens_completion}`}
                            className={`flex items-center gap-1 ${m.tokens_estimated ? 'text-amber-500' : 'text-blue-500'}`}>
                            <Zap style={{ width: 9, height: 9 }} />
                            {m.tokens_estimated ? '≈ ' : ''}{m.tokens_prompt}↑ {m.tokens_completion}↓ = {m.tokens_total}
                          </span>
                        )}
                        {m.tokens_total > 0 && m.tokens_estimated ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 text-[10px]"
                            title={t('aih.estTitle')}>
                            {t('aih.estBadge')}
                          </span>
                        ) : null}
                        {m.response_time > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock style={{ width: 9, height: 9 }} />
                            {(m.response_time/1000).toFixed(1)}s
                          </span>
                        )}
                        {m.accuracy > 0 && (
                          <span className="flex items-center gap-1">
                            <Target style={{ width: 9, height: 9 }} />
                            {m.accuracy}%
                          </span>
                        )}
                        {m.has_data === 1 && (
                          <span className="flex items-center gap-1 text-emerald-600">
                            <Database style={{ width: 9, height: 9 }} />
                            {t('aih.dataBadge')}
                          </span>
                        )}
                      </div>
                    </div>
                    {m.message_role === 'user' && (
                      <div className="w-7 h-7 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <UserIcon style={{ width: 14, height: 14 }} className="text-brand-600" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      </>)}
      <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

// String lokal untuk bagian BARU (per-sumber & per-sesi). Key lama tetap lewat
// t() — hanya tambahan yang memakai tabel ini, supaya gerbang build
// check-i18n.js (paritas id/en/ar) tidak perlu disentuh untuk fitur internal
// yang konsumennya satu orang. Pola yang sama dipakai AIHistorySidebar.
const LU_ALL = {
  id: {
    bySource: 'Per sumber data', source: 'Sumber', sessions: 'Sesi', messages: 'Pesan',
    total: 'Total', bySession: 'Per sesi', session: 'Sesi', dashboard: 'Dashboard',
    untitled: '(tanpa judul)',
    topN: (n, all) => `${n} teratas menurut token dari ${all} sesi`,
    lastAt: 'Aktivitas terakhir', firstAt: 'Mulai', sortHint: 'Klik judul kolom untuk mengurutkan.',
    sourceNote: 'Sumber dinormalisasi: jawaban dari cache semantik (mis. "aws_cost|semantic-cache") dihitung ke sumber aslinya, bukan sebagai sumber terpisah.',
  },
  en: {
    bySource: 'By data source', source: 'Source', sessions: 'Sessions', messages: 'Messages',
    total: 'Total', bySession: 'By session', session: 'Session', dashboard: 'Dashboard',
    untitled: '(untitled)',
    topN: (n, all) => `top ${n} by tokens out of ${all} sessions`,
    lastAt: 'Last activity', firstAt: 'Started', sortHint: 'Click a column header to sort.',
    sourceNote: 'Sources are normalised: semantic-cache answers (e.g. "aws_cost|semantic-cache") count towards their original source, not as a separate one.',
  },
  ar: {
    bySource: 'حسب مصدر البيانات', source: 'المصدر', sessions: 'الجلسات', messages: 'الرسائل',
    total: 'الإجمالي', bySession: 'حسب الجلسة', session: 'الجلسة', dashboard: 'لوحة المعلومات',
    untitled: '(بدون عنوان)',
    topN: (n, all) => `أعلى ${n} حسب الرموز من ${all} جلسة`,
    lastAt: 'آخر نشاط', firstAt: 'البداية', sortHint: 'انقر عنوان العمود للترتيب.',
    sourceNote: 'المصادر موحّدة: إجابات الذاكرة المؤقتة الدلالية تُحتسب ضمن مصدرها الأصلي.',
  },
};

// ── #7 Usage Report: konsumsi token harian + breakdown per provider ──────────
function UsageReport({ usage, loading, days, setDays, t, lang }) {
  const LU     = LU_ALL[lang] || LU_ALL.id;
  const RANGES = [7, 30, 90];
  const daily  = usage?.daily || [];
  const bySource  = usage?.bySource  || [];
  const bySessionRaw = usage?.bySession || [];

  // Urutan default 'tokens desc' MENIRU urutan dari server. Penting: server
  // hanya mengirim 100 sesi TERATAS menurut token, jadi mengurutkan di klien
  // hanya menata ulang 100 baris itu — bukan seluruh 414 sesi. Tanpa disadari,
  // "urutkan menurut tanggal" akan terbaca sebagai "sesi terbaru", padahal
  // artinya "sesi terbaru DI ANTARA 100 yang terberat".
  const [sortKey, setSortKey] = React.useState('tokens');
  const [sortDir, setSortDir] = React.useState('desc');
  const toggleSort = (k) => {
    if (sortKey === k) { setSortDir(d => (d === 'desc' ? 'asc' : 'desc')); return; }
    setSortKey(k);
    // Kolom teks paling berguna dibuka A→Z; kolom angka & waktu dari yang
    // terbesar/terbaru. Memaksa satu arah untuk semuanya membuat klik pertama
    // pada kolom tanggal selalu memberi baris paling tidak menarik.
    setSortDir(k === 'title' || k === 'dashboard' ? 'asc' : 'desc');
  };
  const bySession = React.useMemo(() => {
    const rows = bySessionRaw.slice();
    const dir  = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' || typeof bv === 'number')
        return ((av || 0) - (bv || 0)) * dir;
      return String(av || '').localeCompare(String(bv || ''), undefined, { numeric: true }) * dir;
    });
    return rows;
  }, [bySessionRaw, sortKey, sortDir]);

  const fmtDT = (v) => {
    if (!v) return '—';
    // created_at disimpan sebagai 'YYYY-MM-DD HH:MM:SS' waktu UTC dari
    // datetime('now') SQLite. Tanpa penanda 'Z' sebagian browser membacanya
    // sebagai waktu lokal dan menggeser tampilan beberapa jam.
    const d = new Date(String(v).replace(' ', 'T') + (/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? '' : 'Z'));
    if (isNaN(d)) return String(v);
    return d.toLocaleString(lang === 'en' ? 'en-US' : 'id-ID',
      { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const SortTh = ({ k, children, align = 'right', extra = '' }) => (
    <th className={`text-${align} font-medium py-1.5 ${extra} cursor-pointer select-none
                    hover:text-slate-600 transition-colors`}
      onClick={() => toggleSort(k)}
      title={LU.sortHint}>
      {children}
      <span className={`ml-0.5 ${sortKey === k ? 'text-brand-600' : 'text-transparent'}`}>
        {sortKey === k && sortDir === 'asc' ? '▲' : '▼'}
      </span>
    </th>
  );
  const maxTok = Math.max(1, ...daily.map(d => d.tokens || 0));
  const fmt    = (n) => (n || 0).toLocaleString(lang === 'en' ? 'en-US' : 'id-ID');
  const fmtDay = (s) => {
    const d = new Date(s + 'T00:00:00');
    return isNaN(d) ? s : d.toLocaleDateString(lang === 'en' ? 'en-US' : 'id-ID', { day: '2-digit', month: 'short' });
  };

  // Provider aktif dalam jendela + total per provider (untuk legenda/ringkasan).
  const provTotals = {};
  (usage?.byProviderDaily || []).forEach(r => {
    provTotals[r.provider] = (provTotals[r.provider] || 0) + (r.tokens || 0);
  });
  const provList = Object.entries(provTotals).sort((a, b) => b[1] - a[1]);

  // Format biaya USD: <$0.01 tampil "<$0.01"; selebihnya 2–4 desimal adaptif.
  const fmtUsd = (v) => {
    const n = Number(v) || 0;
    if (n === 0) return '$0.00';
    if (n < 0.01) return '<$0.01';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: n < 1 ? 4 : 2 });
  };
  const byModel   = usage?.byModel || [];
  const hasPriced = byModel.some(m => m.priced);

  return (
    <div className="mb-6">
      {/* Rentang waktu */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-slate-500">{t('aih.usageRange')}:</span>
        {RANGES.map(r => (
          <button key={r} onClick={() => setDays(r)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
              days === r ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {t('aih.usageDays', { n: r })}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">
          {t('aih.usageTotal')}: <span className="font-bold text-slate-800">{fmt(usage?.totalTokens)}</span> {t('ai.tokens')}
          <span className="mx-2 text-slate-300">·</span>
          {t('aih.usageCostTotal')}: <span className="font-bold text-emerald-700">{fmtUsd(usage?.totalCost)}</span>
        </span>
      </div>

      {loading && (
        <div className="p-8 text-center text-slate-400">
          <Loader2 style={{ width: 20, height: 20 }} className="animate-spin mx-auto mb-2" />
          {t('common.loading')}
        </div>
      )}

      {!loading && daily.length === 0 && (
        <div className="p-8 text-center text-slate-400 text-sm bg-white rounded-xl border border-slate-100">
          {t('aih.usageEmpty')}
        </div>
      )}

      {!loading && daily.length > 0 && (
        <>
          {/* Ringkasan per provider */}
          {provList.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {provList.map(([prov, tok]) => (
                <div key={prov} className="px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-500 font-medium">{prov}</div>
                  <div className="text-sm font-bold text-slate-800 mt-0.5">
                    {fmt(tok)} <span className="text-xs text-slate-400 font-normal">{t('ai.tokens')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Estimasi biaya per model (input & output beda tarif) */}
          {byModel.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-4 mb-5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {t('aih.usageCostTitle')}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-max">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left font-medium py-1.5 pr-4">{t('aih.colModel')}</th>
                      <th className="text-right font-medium py-1.5 px-3">{t('aih.colInput')}</th>
                      <th className="text-right font-medium py-1.5 px-3">{t('aih.colOutput')}</th>
                      <th className="text-right font-medium py-1.5 pl-3">{t('aih.colCost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byModel.map((m, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="py-1.5 pr-4">
                          <span className="text-slate-700">{m.provider}</span>
                          <span className="text-slate-400"> / {m.model}</span>
                          {!m.priced && <span className="ml-1.5 text-[10px] text-amber-600 bg-amber-50 px-1 rounded">{t('aih.costUnpriced')}</span>}
                        </td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(m.in_tokens)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(m.out_tokens)}</td>
                        <td className="text-right py-1.5 pl-3 font-medium text-emerald-700">{fmtUsd(m.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 font-semibold text-slate-800">
                      <td className="py-2 pr-4">{t('aih.costTotalRow')}</td>
                      <td className="text-right py-2 px-3">{fmt(byModel.reduce((a, m) => a + m.in_tokens, 0))}</td>
                      <td className="text-right py-2 px-3">{fmt(byModel.reduce((a, m) => a + m.out_tokens, 0))}</td>
                      <td className="text-right py-2 pl-3 text-emerald-700">{fmtUsd(usage?.totalCost)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                {t('aih.costDisclaimer')}
              </p>
            </div>
          )}

          {/* Bar harian (CSS, tanpa dependency chart) — DUA segmen: input & output.
              Panjang bar tetap mengacu ke total harian, jadi perbandingan
              antar-hari tidak berubah; yang bertambah hanya komposisinya.
              Ini penting karena input dan output berbeda tarif — hari dengan
              total sama bisa berbeda biaya beberapa kali lipat. */}
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <BarChart3 style={{ width: 14, height: 14 }} />
              {t('aih.usageDailyTitle')}
            </div>
            <div className="flex items-center gap-3 mb-3 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-2.5 rounded-sm bg-brand-500" /> input
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-2.5 rounded-sm bg-amber-400" /> output
              </span>
            </div>
            <div className="space-y-1.5 max-h-[calc(100vh-420px)] overflow-y-auto">
              {daily.slice().reverse().map(d => {
                const tot = d.tokens || 0;
                const inn = d.prompt_tokens || 0;
                const out = d.completion_tokens || 0;
                // Skala dihitung dari in+out, bukan dari `tokens`. Keduanya
                // biasanya sama, tapi baris lama bisa punya tokens_total terisi
                // sementara pecahannya nol — tanpa ini bar-nya kosong melompong
                // padahal angkanya ada.
                const pair = inn + out;
                const wIn  = pair ? (inn / maxTok) * 100 : 0;
                const wOut = pair ? (out / maxTok) * 100 : 0;
                return (
                  <div key={d.day} className="flex items-center gap-2 text-xs">
                    <div className="w-16 text-slate-500 flex-shrink-0">{fmtDay(d.day)}</div>
                    <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden flex">
                      <div className="bg-brand-500 h-full" style={{ width: `${wIn}%` }}
                        title={`input ${fmt(inn)}`} />
                      <div className="bg-amber-400 h-full" style={{ width: `${wOut}%` }}
                        title={`output ${fmt(out)}`} />
                    </div>
                    <div className="w-44 text-right text-slate-700 font-medium flex-shrink-0">
                      {fmt(tot)}
                      <span className="text-slate-400 font-normal">
                        {' '}({fmt(inn)}↑ {fmt(out)}↓ · {d.messages}m)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Per DATA SOURCE ────────────────────────────────────────────── */}
          {bySource.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-4 mt-5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Database style={{ width: 14, height: 14 }} />
                {LU.bySource}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-max">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left  font-medium py-1.5 pr-4">{LU.source}</th>
                      <th className="text-right font-medium py-1.5 px-3">{LU.sessions}</th>
                      <th className="text-right font-medium py-1.5 px-3">{LU.messages}</th>
                      <th className="text-right font-medium py-1.5 px-3">{t('aih.colInput')}</th>
                      <th className="text-right font-medium py-1.5 px-3">{t('aih.colOutput')}</th>
                      <th className="text-right font-medium py-1.5 pl-3">{LU.total}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bySource.map((s, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="py-1.5 pr-4 text-slate-700">
                          {s.source}
                          {s.cached_msgs > 0 && (
                            <span className="ml-1.5 text-[10px] text-sky-700 bg-sky-50 px-1 rounded">
                              {s.cached_msgs} cache
                            </span>
                          )}
                        </td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.sessions)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.messages)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.in_tokens)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.out_tokens)}</td>
                        <td className="text-right py-1.5 pl-3 font-medium text-slate-800">{fmt(s.tokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{LU.sourceNote}</p>
            </div>
          )}

          {/* ── Per SESI ───────────────────────────────────────────────────── */}
          {bySession.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-4 mt-5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MessageSquare style={{ width: 14, height: 14 }} />
                {LU.bySession}
                <span className="normal-case tracking-normal font-normal text-slate-400">
                  — {LU.topN(bySession.length, usage?.sessionTotal || bySession.length)}
                </span>
              </div>
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-xs min-w-max">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-slate-400 border-b border-slate-100">
                      <SortTh k="title"      align="left"  extra="pr-4">{LU.session}</SortTh>
                      <SortTh k="dashboard"  align="left"  extra="px-3">{LU.dashboard}</SortTh>
                      <SortTh k="last_at"    align="left"  extra="px-3">{LU.lastAt}</SortTh>
                      <SortTh k="messages"   extra="px-3">{LU.messages}</SortTh>
                      <SortTh k="in_tokens"  extra="px-3">{t('aih.colInput')}</SortTh>
                      <SortTh k="out_tokens" extra="px-3">{t('aih.colOutput')}</SortTh>
                      <SortTh k="tokens"     extra="px-3">{LU.total}</SortTh>
                      <SortTh k="cost_usd"   extra="pl-3">{t('aih.colCost')}</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {bySession.map(s => (
                      <tr key={s.session_id} className="border-b border-slate-50 last:border-0">
                        <td className="py-1.5 pr-4 max-w-[320px]">
                          <div className="text-slate-700 truncate" title={s.title}>
                            {s.title || <span className="text-slate-400">{LU.untitled}</span>}
                          </div>
                          <div className="text-[10px] text-slate-400 truncate">{s.username}</div>
                        </td>
                        <td className="py-1.5 px-3 text-slate-500">{s.dashboard || '—'}</td>
                        <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">
                          {fmtDT(s.last_at)}
                          {s.first_at && s.first_at !== s.last_at && (
                            <div className="text-[10px] text-slate-400">{LU.firstAt} {fmtDT(s.first_at)}</div>
                          )}
                        </td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.messages)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.in_tokens)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(s.out_tokens)}</td>
                        <td className="text-right py-1.5 px-3 font-medium text-slate-800">{fmt(s.tokens)}</td>
                        <td className="text-right py-1.5 pl-3 text-emerald-700">{fmtUsd(s.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Tab ANALYTICS ────────────────────────────────────────────────────────────
// Empat pengukuran, semuanya dari kolom yang sudah lama ada di ai_chat_history.
// Tidak ada instrumentasi baru, jadi seluruh riwayat lama ikut terhitung sejak
// tab ini pertama kali dibuka.
//
// Yang SENGAJA tidak ada di sini: waktu respons. Jalur share publik menulis
// response_time = 0 secara hardcode, sehingga p50/p95 apa pun akan terlihat
// membaik justru saat trafik publik naik. Angka yang menyesatkan lebih buruk
// daripada tidak ada angka.
const LA_ALL = {
  id: {
    range: 'Rentang', days: (n) => `${n} hari`, empty: 'Belum ada data pada rentang ini.',
    noDataTitle: 'Jawaban yang tidak menyentuh data',
    noDataLead: 'Bagian jawaban asisten yang dihasilkan tanpa satu pun baris data. Sebagian wajar (sapaan, pertanyaan balik); yang perlu dicurigai adalah rasio tinggi pada dashboard yang seharusnya selalu menjawab dari database.',
    fpTitle: 'Sidik jari token',
    fpLead: 'Jawaban yang lewat text-to-SQL membawa skema, jadi token input-nya besar. Jawaban yang tidak menyentuh data hanya membawa prompt pendek. Bila median kelompok kedua jauh lebih kecil, itu bukan kebetulan — itu tandanya.',
    fpWith: 'Median input · dengan data', fpWithout: 'Median input · tanpa data',
    fpSamples: 'sampel', fpTrunc: 'Sampel dipotong pada 100.000 baris terbaru.',
    cacheTitle: 'Cache semantik', cacheLead: 'Bagian jawaban yang dilayani dari cache, bukan dari pemanggilan model baru.',
    cacheHits: 'hit', cacheOf: 'dari',
    cacheZero: 'Nol hit pada rentang ini. Bila ini bertahan, ambang kemiripan kemungkinan dipasang di atas skor tertinggi yang pernah benar-benar terjadi — menurunkannya saja belum tentu menolong bila kolam cache juga terfragmentasi.',
    estTitle: 'Token estimasi', estLead: 'Bagian token yang ditaksir sendiri karena provider tidak mengirim angka usage. Ini menentukan seberapa jauh tabel biaya di tab Usage Report boleh dipercaya.',
    estOf: 'token ditaksir dari',
    retryTitle: 'Pertanyaan yang diulang', retryLead: 'Pertanyaan yang diedit ulang oleh pengguna (superseded). Rasio tinggi berarti jawaban pertama sering tidak memadai.',
    colDash: 'Dashboard', colAnswers: 'Jawaban', colNoData: 'Tanpa data', colPct: '%',
    colAsked: 'Pertanyaan', colEdited: 'Diedit',
    outTitle: 'Sisi output',
    outLead: 'Output tidak dipakai sebagai penanda jalur — selisihnya terlalu tipis untuk itu. Yang berguna darinya adalah porsi biaya dan jawaban yang praktis kosong.',
    outShare: 'Porsi token output', outEmpty: 'Jawaban kosong', outOf: 'dari',
    outUnreported: 'Usage tak dilaporkan', outUnreportedNote: 'jawaban dengan token output 0',
    outFoot: (n) => `Kosong = panjang jawaban di bawah ${n} karakter. Diukur dari isi jawaban, BUKAN dari token output 0 — nilai nol di sana lebih sering berarti provider tidak melaporkan usage daripada model benar-benar diam. Tarif output lazimnya 3-5x tarif input, jadi porsi token kecil bisa berarti porsi biaya besar.`,
  },
  en: {
    range: 'Range', days: (n) => `${n} days`, empty: 'No data in this range yet.',
    noDataTitle: 'Answers that never touched data',
    noDataLead: 'Share of assistant answers produced without a single row of data. Some is expected (greetings, clarifying questions); what deserves suspicion is a high ratio on a dashboard that should always answer from the database.',
    fpTitle: 'Token fingerprint',
    fpLead: 'Answers that go through text-to-SQL carry the schema, so their input token count is large. Answers that never touch data carry only a short prompt. If the second median is far smaller, that is not coincidence — that is the tell.',
    fpWith: 'Median input · with data', fpWithout: 'Median input · without data',
    fpSamples: 'samples', fpTrunc: 'Sample capped at the 100,000 most recent rows.',
    cacheTitle: 'Semantic cache', cacheLead: 'Share of answers served from cache rather than a fresh model call.',
    cacheHits: 'hits', cacheOf: 'of',
    cacheZero: 'Zero hits in this range. If that holds, the similarity threshold is likely set above the highest score that has ever actually occurred — and lowering it alone may not help if the cache pool is also fragmented.',
    estTitle: 'Estimated tokens', estLead: 'Share of tokens that were guessed locally because the provider returned no usage figures. This sets how far the cost table in Usage Report can be trusted.',
    estOf: 'tokens estimated out of',
    retryTitle: 'Re-asked questions', retryLead: 'Questions the user edited and resubmitted (superseded). A high ratio means first answers often fall short.',
    colDash: 'Dashboard', colAnswers: 'Answers', colNoData: 'No data', colPct: '%',
    colAsked: 'Asked', colEdited: 'Edited',
    outTitle: 'Output side',
    outLead: 'Output is not used as a path fingerprint — the gap is too narrow for that. What it is good for is cost weight and answers that came back essentially empty.',
    outShare: 'Output token share', outEmpty: 'Empty answers', outOf: 'of',
    outUnreported: 'Usage not reported', outUnreportedNote: 'answers with 0 output tokens',
    outFoot: (n) => `Empty = answer shorter than ${n} characters. Measured from answer content, NOT from zero output tokens — a zero there more often means the provider reported no usage than that the model actually said nothing. Output rates are typically 3-5x input rates, so a small token share can mean a large cost share.`,
  },
  ar: {
    range: 'النطاق', days: (n) => `${n} يوم`, empty: 'لا توجد بيانات في هذا النطاق.',
    noDataTitle: 'إجابات لم تمس البيانات',
    noDataLead: 'نسبة إجابات المساعد التي أُنتجت دون أي صف بيانات. بعضها متوقع؛ والمثير للشك هو نسبة عالية في لوحة يُفترض أن تجيب دائمًا من قاعدة البيانات.',
    fpTitle: 'بصمة الرموز',
    fpLead: 'الإجابات التي تمر عبر تحويل النص إلى SQL تحمل المخطط، لذا رموز إدخالها كبيرة. أما التي لا تمس البيانات فتحمل مطالبة قصيرة فقط.',
    fpWith: 'وسيط الإدخال · مع بيانات', fpWithout: 'وسيط الإدخال · بدون بيانات',
    fpSamples: 'عينات', fpTrunc: 'العينة محدودة بأحدث 100000 صف.',
    cacheTitle: 'الذاكرة المؤقتة الدلالية', cacheLead: 'نسبة الإجابات المقدَّمة من الذاكرة المؤقتة بدل استدعاء جديد للنموذج.',
    cacheHits: 'إصابة', cacheOf: 'من',
    cacheZero: 'صفر إصابة في هذا النطاق. إن استمر ذلك، فالأرجح أن عتبة التشابه أعلى من أعلى درجة حدثت فعليًا.',
    estTitle: 'رموز مُقدَّرة', estLead: 'نسبة الرموز التي قُدِّرت محليًا لأن المزوّد لم يُرسل أرقام الاستخدام.',
    estOf: 'رمزًا مُقدَّرًا من',
    retryTitle: 'أسئلة أُعيد طرحها', retryLead: 'أسئلة عدّلها المستخدم وأعاد إرسالها. النسبة العالية تعني أن الإجابات الأولى غالبًا غير كافية.',
    colDash: 'لوحة المعلومات', colAnswers: 'إجابات', colNoData: 'بدون بيانات', colPct: '٪',
    colAsked: 'أسئلة', colEdited: 'مُعدَّلة',
    outTitle: 'جانب المخرجات',
    outLead: 'لا تُستخدم المخرجات كبصمة للمسار؛ الفارق ضيق جدًا. فائدتها في وزن التكلفة والإجابات الفارغة عمليًا.',
    outShare: 'حصة رموز المخرجات', outEmpty: 'إجابات فارغة', outOf: 'من',
    outUnreported: 'استخدام غير مُبلَّغ', outUnreportedNote: 'إجابات برموز مخرجات صفر',
    outFoot: (n) => `فارغة = إجابة أقصر من ${n} حرفًا. تُقاس من محتوى الإجابة لا من رموز المخرجات الصفرية. أسعار المخرجات عادة 3-5 أضعاف المدخلات.`,
  },
};

function AnalyticsReport({ data, loading, days, setDays, lang }) {
  const L      = LA_ALL[lang] || LA_ALL.id;
  const RANGES = [7, 30, 90];
  const fmt    = (n) => (n || 0).toLocaleString(lang === 'en' ? 'en-US' : 'id-ID');
  const pct    = (n) => `${(Number(n) || 0).toFixed(1)}%`;

  const noData = data?.noData || [];
  const retry  = data?.retry  || [];
  const fp     = data?.promptFingerprint || {};
  const cache  = data?.cache     || {};
  const est    = data?.estimated || {};
  const out    = data?.output    || {};

  // Warna ambang dipilih longgar dengan sengaja. Angka ini adalah PETUNJUK,
  // bukan SLA — menandai merah pada 30% akan membuat dashboard yang memang
  // banyak dipakai untuk tanya-jawab umum tampak rusak padahal tidak.
  const tone = (p) => (p >= 60 ? 'text-red-600' : p >= 30 ? 'text-amber-600' : 'text-slate-700');

  const Card = ({ title, lead, children }) => (
    <div className="bg-white rounded-xl border border-slate-100 p-4 mb-5">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{title}</div>
      <p className="text-[11px] text-slate-400 leading-relaxed mb-3">{lead}</p>
      {children}
    </div>
  );

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-slate-500">{L.range}:</span>
        {RANGES.map(r => (
          <button key={r} onClick={() => setDays(r)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
              days === r ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {L.days(r)}
          </button>
        ))}
      </div>

      {loading && (
        <div className="p-8 text-center text-slate-400">
          <Loader2 style={{ width: 20, height: 20 }} className="animate-spin mx-auto mb-2" />
          Loading…
        </div>
      )}

      {!loading && !noData.length && !retry.length && (
        <div className="p-8 text-center text-slate-400 text-sm bg-white rounded-xl border border-slate-100">
          {L.empty}
        </div>
      )}

      {!loading && (noData.length > 0 || retry.length > 0) && (
        <>
          {/* 1 · Jawaban tanpa data */}
          <Card title={L.noDataTitle} lead={L.noDataLead}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-max">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="text-left  font-medium py-1.5 pr-4">{L.colDash}</th>
                    <th className="text-right font-medium py-1.5 px-3">{L.colAnswers}</th>
                    <th className="text-right font-medium py-1.5 px-3">{L.colNoData}</th>
                    <th className="text-right font-medium py-1.5 pl-3">{L.colPct}</th>
                  </tr>
                </thead>
                <tbody>
                  {noData.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-1.5 pr-4 text-slate-700">{r.dashboard}</td>
                      <td className="text-right py-1.5 px-3 text-slate-600">{fmt(r.answers)}</td>
                      <td className="text-right py-1.5 px-3 text-slate-600">{fmt(r.no_data)}</td>
                      <td className={`text-right py-1.5 pl-3 font-semibold ${tone(r.pct_no_data)}`}>
                        {pct(r.pct_no_data)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 2 · Sidik jari token */}
          <Card title={L.fpTitle} lead={L.fpLead}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="text-[11px] text-slate-500">{L.fpWith}</div>
                <div className="text-lg font-bold text-slate-800 mt-0.5">{fmt(fp.median_with_data)}</div>
                <div className="text-[10px] text-slate-400">{fmt(fp.n_with_data)} {L.fpSamples}</div>
              </div>
              <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="text-[11px] text-slate-500">{L.fpWithout}</div>
                <div className="text-lg font-bold text-amber-600 mt-0.5">{fmt(fp.median_without_data)}</div>
                <div className="text-[10px] text-slate-400">{fmt(fp.n_without_data)} {L.fpSamples}</div>
              </div>
            </div>
            {fp.truncated && (
              <p className="text-[11px] text-amber-600 mt-2">{L.fpTrunc}</p>
            )}
          </Card>

          {/* 3 · Cache + 4 · Estimasi, berdampingan */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title={L.cacheTitle} lead={L.cacheLead}>
              <div className="text-2xl font-bold text-slate-800">
                {pct(cache.pct)}
                <span className="text-xs font-normal text-slate-400 ml-2">
                  {fmt(cache.hits)} {L.cacheHits} {L.cacheOf} {fmt(cache.answers)}
                </span>
              </div>
              {cache.answers > 0 && (cache.hits || 0) === 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2 mt-2.5 leading-relaxed">
                  {L.cacheZero}
                </p>
              )}
            </Card>

            <Card title={L.estTitle} lead={L.estLead}>
              <div className="text-2xl font-bold text-slate-800">
                {pct(est.pct_tokens)}
                <span className="text-xs font-normal text-slate-400 ml-2">
                  {fmt(est.est_tokens)} {L.estOf} {fmt(est.tokens)}
                </span>
              </div>
            </Card>
          </div>

          {/* 4 · Pertanyaan diulang */}
          {retry.length > 0 && (
            <Card title={L.retryTitle} lead={L.retryLead}>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-max">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left  font-medium py-1.5 pr-4">{L.colDash}</th>
                      <th className="text-right font-medium py-1.5 px-3">{L.colAsked}</th>
                      <th className="text-right font-medium py-1.5 px-3">{L.colEdited}</th>
                      <th className="text-right font-medium py-1.5 pl-3">{L.colPct}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retry.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="py-1.5 pr-4 text-slate-700">{r.dashboard}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(r.asked)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(r.superseded)}</td>
                        <td className={`text-right py-1.5 pl-3 font-semibold ${tone(r.pct)}`}>{pct(r.pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* 5 · Sisi output */}
          <Card title={L.outTitle} lead={L.outLead}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="text-[11px] text-slate-500">{L.outShare}</div>
                <div className="text-lg font-bold text-amber-600 mt-0.5">{pct(out.out_share_pct)}</div>
                <div className="text-[10px] text-slate-400">
                  {fmt(out.tokens_out)} / {fmt((out.tokens_in || 0) + (out.tokens_out || 0))}
                </div>
              </div>
              <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="text-[11px] text-slate-500">{L.outEmpty}</div>
                <div className={`text-lg font-bold mt-0.5 ${tone(out.empty_pct)}`}>{pct(out.empty_pct)}</div>
                <div className="text-[10px] text-slate-400">
                  {fmt(out.empty_answers)} {L.outOf} {fmt(out.answers)}
                </div>
              </div>
              <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="text-[11px] text-slate-500">{L.outUnreported}</div>
                <div className="text-lg font-bold text-slate-800 mt-0.5">{fmt(out.unreported)}</div>
                <div className="text-[10px] text-slate-400">{L.outUnreportedNote}</div>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2.5 leading-relaxed">
              {L.outFoot(out.empty_chars || 20)}
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

// ── Tab COST FORECAST ────────────────────────────────────────────────────────
// Dasar penyusunan anggaran AI. Prinsipnya satu: TOKEN ADALAH FAKTA, TARIF
// ADALAH ASUMSI. Backend hanya mengirim token; setiap perkalian dengan uang
// terjadi di sini, dari tarif yang diisi sendiri oleh pengguna.
//
// Ini bukan pemisahan kosmetik. LLM_PRICING di backend tidak mengenal model yang
// dipakai jalur AI Coding ("claude-sonnet-5", "gpt-5.5" lewat proxy
// OpenAI-compatible), dan cabang "tak dikenal" mengembalikan tarif 0. Kalau
// halaman ini memakai angka itu, beban terberat akan tampil sebagai $0 dan
// proposal anggaran berdiri di atas angka yang bolong. Di sini model tanpa
// tarif justru DITONJOLKAN sebagai peringatan, bukan dihitung nol diam-diam.
//
// Tarif disimpan di tabel `ai_model_rates` (bukan lagi localStorage): instalasi
// ini memakai belasan model berdampingan, dan mengetik ulang puluhan kolom tiap
// ganti browser bukan alur kerja yang bisa dipertahankan. Perubahan tarif juga
// masuk Activity Log, karena tarif adalah dasar angka anggaran — bukan
// preferensi tampilan.

const LF_ALL = {
  id: {
    range: 'Rentang', days: (n) => `${n} hari`, empty: 'Belum ada pemakaian pada rentang ini.',
    ratesTitle: 'Tarif model (USD per 1 juta token)',
    ratesLead: 'Diisi manual dan disimpan di browser ini saja. Backend tidak menghitung biaya sama sekali — angka di bawah sepenuhnya berasal dari tarif yang Anda isi.',
    colModel: 'Provider / Model', colIn: 'Token in', colOut: 'Token out',
    colRateIn: 'Tarif in', colRateOut: 'Tarif out', colCost: 'Biaya',
    unpriced: 'tarif belum diisi',
    warnTitle: 'Sebagian trafik belum punya tarif',
    warnBody: (t, p) => `${t} token (${p}% dari seluruh pemakaian) berasal dari model yang tarifnya belum Anda isi. Selama itu kosong, seluruh angka biaya di halaman ini menghitungnya sebagai nol — dan proposal yang dibuat darinya akan lebih rendah dari kenyataan.`,
    windowTitle: 'Biaya pada rentang ini',
    windowLead: 'Hasil perkalian token nyata dengan tarif yang Anda isi. Belum ada proyeksi apa pun di sini.',
    wCost: 'Total biaya', wOutShare: 'Porsi biaya dari output', wTokens: 'Total token',
    outNote: 'Output biasanya beberapa kali lebih mahal per token daripada input — porsi tokennya kecil, porsi biayanya belum tentu.',
    projTitle: 'Proyeksi bulanan',
    projLead: 'Disajikan sebagai RENTANG, bukan satu angka. Rata-rata harian menyembunyikan puncak, dan anggaran yang disusun dari rata-rata akan jebol pada bulan tersibuk.',
    pAvg: 'Dari rata-rata harian', pMedian: 'Dari median harian', pMax: 'Dari hari tersibuk',
    perMonth: '/bulan', basisTitle: 'Dasar hari',
    basisCal: 'Kalender (30 hari)', basisActive: 'Hari aktif',
    basisNote: (a, w) => `Tercatat ${a} hari beraktivitas dari ${w} hari jendela. Pilih "hari aktif" bila pemakaian hanya pada hari kerja — memakai kalender akan melebihkan, memakai hari aktif pada pemakaian harian akan mengurangkan.`,
    simTitle: 'Simulator skenario',
    simLead: 'Nilai awal diambil dari pemakaian nyata Anda. Geser asumsinya untuk menyusun skenario anggaran.',
    simUsers: 'Jumlah user', simSess: 'Sesi per user per hari',
    simIn: 'Token input per sesi', simOut: 'Token output per sesi',
    simDays: 'Hari pakai per bulan', simRateIn: 'Tarif in (USD/1jt)', simRateOut: 'Tarif out (USD/1jt)',
    simSeed: 'Isi ulang dari data nyata', simMedian: 'median', simAvg: 'rata-rata',
    simResult: 'Estimasi biaya bulanan', simTokens: 'Token per bulan',
    idrTitle: 'Kurs USD → IDR', idrNote: 'Opsional, untuk lampiran proposal.',
    perSessTitle: 'Sebaran per sesi', perSessLead: 'Rata-rata dan median sengaja ditampilkan berdampingan. Bila keduanya jauh berbeda, pemakaian Anda didominasi sedikit sesi berat — dan rata-rata bukan dasar anggaran yang aman.',
    sAvg: 'Rata-rata', sMedian: 'Median', sP90: 'Persentil 90', sMax: 'Maksimum',
    userTitle: 'Per user', userLead: 'Trafik share link tercatat atas nama dashboard, bukan orang — ditandai "publik". Jangan dihitung sebagai satu karyawan saat menyusun anggaran per-kepala.',
    colUser: 'User', colSess: 'Sesi', colMsg: 'Pesan', public: 'publik',
    export: 'Ekspor XLSX',
    saving: 'menyimpan…', seed: 'Isi dari harga daftar',
    seedDone: (a, sk) => `${a} tarif terisi dari harga daftar bawaan. ${sk} model dilewati karena tidak ada di daftar itu — model lewat proxy pihak ketiga memang harus diisi manual.\n\nAngka hasil isian ini adalah harga DAFTAR on-demand. Verifikasi terhadap tagihan nyata sebelum dipakai di proposal.`,
  },
  en: {
    range: 'Range', days: (n) => `${n} days`, empty: 'No usage in this range yet.',
    ratesTitle: 'Model rates (USD per 1M tokens)',
    ratesLead: 'Entered manually and stored in this browser only. The backend computes no cost at all — every figure below comes entirely from the rates you enter.',
    colModel: 'Provider / Model', colIn: 'Tokens in', colOut: 'Tokens out',
    colRateIn: 'Rate in', colRateOut: 'Rate out', colCost: 'Cost',
    unpriced: 'no rate set',
    warnTitle: 'Some traffic has no rate',
    warnBody: (t, p) => `${t} tokens (${p}% of all usage) come from models you have not priced. While that is blank, every cost figure on this page counts them as zero — and a proposal built from it will come in under reality.`,
    windowTitle: 'Cost over this range',
    windowLead: 'Real tokens multiplied by the rates you entered. No projection involved yet.',
    wCost: 'Total cost', wOutShare: 'Share of cost from output', wTokens: 'Total tokens',
    outNote: 'Output usually costs several times more per token than input — its token share is small, its cost share need not be.',
    projTitle: 'Monthly projection',
    projLead: 'Presented as a RANGE, not one number. A daily average hides the peaks, and a budget built from the average breaks in the busiest month.',
    pAvg: 'From daily average', pMedian: 'From daily median', pMax: 'From busiest day',
    perMonth: '/month', basisTitle: 'Day basis',
    basisCal: 'Calendar (30 days)', basisActive: 'Active days',
    basisNote: (a, w) => `${a} active days recorded out of a ${w}-day window. Pick "active days" if usage only happens on working days — calendar will overstate, and active days will understate genuinely daily usage.`,
    simTitle: 'Scenario simulator',
    simLead: 'Seeded from your real usage. Move the assumptions to build budget scenarios.',
    simUsers: 'Number of users', simSess: 'Sessions per user per day',
    simIn: 'Input tokens per session', simOut: 'Output tokens per session',
    simDays: 'Usage days per month', simRateIn: 'Rate in (USD/1M)', simRateOut: 'Rate out (USD/1M)',
    simSeed: 'Reseed from real data', simMedian: 'median', simAvg: 'average',
    simResult: 'Estimated monthly cost', simTokens: 'Tokens per month',
    idrTitle: 'USD → IDR rate', idrNote: 'Optional, for the proposal attachment.',
    perSessTitle: 'Per-session spread', perSessLead: 'Average and median are shown side by side on purpose. If they differ sharply, your usage is dominated by a few heavy sessions — and the average is not a safe budgeting base.',
    sAvg: 'Average', sMedian: 'Median', sP90: '90th percentile', sMax: 'Maximum',
    userTitle: 'Per user', userLead: 'Share-link traffic is recorded under the dashboard name, not a person — flagged "public". Do not count it as one employee when budgeting per head.',
    colUser: 'User', colSess: 'Sessions', colMsg: 'Messages', public: 'public',
    export: 'Export XLSX',
    saving: 'saving…', seed: 'Fill from list prices',
    seedDone: (a, sk) => `${a} rates filled from the built-in list prices. ${sk} models were skipped because they are not in that list — third-party proxy models have to be entered by hand.\n\nThese are LIST on-demand prices. Verify them against a real invoice before using them in a proposal.`,
  },
  ar: {
    range: 'النطاق', days: (n) => `${n} يوم`, empty: 'لا يوجد استخدام في هذا النطاق.',
    ratesTitle: 'أسعار النماذج (دولار لكل مليون رمز)',
    ratesLead: 'تُدخل يدويًا وتُحفظ في هذا المتصفح فقط. الخادم لا يحسب أي تكلفة.',
    colModel: 'المزوّد / النموذج', colIn: 'رموز الإدخال', colOut: 'رموز المخرجات',
    colRateIn: 'سعر الإدخال', colRateOut: 'سعر المخرجات', colCost: 'التكلفة',
    unpriced: 'بدون سعر',
    warnTitle: 'بعض الحركة بلا سعر',
    warnBody: (t, p) => `${t} رمزًا (${p}٪ من الاستخدام) من نماذج لم تُحدَّد أسعارها، وتُحتسب صفرًا الآن.`,
    windowTitle: 'التكلفة في هذا النطاق',
    windowLead: 'رموز فعلية مضروبة في الأسعار التي أدخلتها.',
    wCost: 'إجمالي التكلفة', wOutShare: 'حصة المخرجات من التكلفة', wTokens: 'إجمالي الرموز',
    outNote: 'تكلفة المخرجات عادة أضعاف المدخلات لكل رمز.',
    projTitle: 'التوقع الشهري',
    projLead: 'يُعرض كنطاق لا كرقم واحد؛ المتوسط يخفي الذروة.',
    pAvg: 'من المتوسط اليومي', pMedian: 'من الوسيط اليومي', pMax: 'من أكثر الأيام ازدحامًا',
    perMonth: '/شهر', basisTitle: 'أساس الأيام',
    basisCal: 'تقويمي (30 يومًا)', basisActive: 'الأيام النشطة',
    basisNote: (a, w) => `${a} يومًا نشطًا من نافذة ${w} يومًا.`,
    simTitle: 'محاكي السيناريو',
    simLead: 'القيم الأولية من استخدامك الفعلي.',
    simUsers: 'عدد المستخدمين', simSess: 'جلسات لكل مستخدم يوميًا',
    simIn: 'رموز الإدخال لكل جلسة', simOut: 'رموز المخرجات لكل جلسة',
    simDays: 'أيام الاستخدام شهريًا', simRateIn: 'سعر الإدخال', simRateOut: 'سعر المخرجات',
    simSeed: 'إعادة التعبئة من البيانات', simMedian: 'الوسيط', simAvg: 'المتوسط',
    simResult: 'التكلفة الشهرية المقدَّرة', simTokens: 'الرموز شهريًا',
    idrTitle: 'سعر الصرف دولار → روبية', idrNote: 'اختياري.',
    perSessTitle: 'توزيع الجلسات', perSessLead: 'المتوسط والوسيط جنبًا إلى جنب عن قصد.',
    sAvg: 'المتوسط', sMedian: 'الوسيط', sP90: 'المئين 90', sMax: 'الأقصى',
    userTitle: 'لكل مستخدم', userLead: 'حركة روابط المشاركة مسجَّلة باسم اللوحة لا باسم شخص.',
    colUser: 'المستخدم', colSess: 'جلسات', colMsg: 'رسائل', public: 'عام',
    export: 'تصدير XLSX',
    saving: 'جارٍ الحفظ…', seed: 'التعبئة من أسعار القائمة',
    seedDone: (a, sk) => `تمت تعبئة ${a} سعرًا. تم تخطي ${sk} نموذجًا. تحقق من الأسعار قبل استخدامها.`,
  },
};

function CostForecast({ data, loading, days, setDays, lang }) {
  const L      = LF_ALL[lang] || LF_ALL.id;
  const RANGES = [7, 30, 90];
  const locale = lang === 'en' ? 'en-US' : 'id-ID';
  const fmt    = (n) => Math.round(Number(n) || 0).toLocaleString(locale);
  const usd    = (n) => {
    const v = Number(n) || 0;
    if (v === 0) return '$0.00';
    if (v < 0.01) return '<$0.01';
    return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: v < 1 ? 4 : 2 });
  };

  const byModel = React.useMemo(() => data?.byModel || [], [data]);
  const daily   = data?.daily   || [];
  const byUser  = data?.byUser  || [];
  const perSess = data?.perSession || {};

  // ── Tarif per model, dari DB ───────────────────────────────────────────────
  const [rates, setRates]   = React.useState({});
  const [saving, setSaving] = React.useState('');
  const rkey = (m) => `${m.provider}/${m.model}`;

  const loadRates = React.useCallback(async () => {
    try {
      const res = await api.get('/api/ai/model-rates');
      const map = {};
      for (const r of (res.data?.rates || []))
        map[`${r.provider}/${r.model}`] = { in: String(r.rate_in ?? ''), out: String(r.rate_out ?? '') };
      setRates(map);
    } catch (e) { console.warn('[CostForecast] load rates:', e.message); }
  }, []);
  React.useEffect(() => { loadRates(); }, [loadRates]);

  const rateOf = (m) => rates[rkey(m)] || { in: '', out: '' };

  // Ketikan diterapkan ke state SEKARANG dan disimpan BELAKANGAN (debounce).
  // Menyimpan tiap penekanan tombol akan mengirim satu PUT per digit — "0.06"
  // menjadi empat tulisan DB, tiga di antaranya nilai setengah jadi yang sempat
  // tercatat di Activity Log.
  const saveTimers = React.useRef({});
  const setRate = (m, side, val) => {
    const k = rkey(m);
    setRates(prev => {
      const next = { ...prev, [k]: { ...(prev[k] || { in: '', out: '' }), [side]: val } };
      const cur  = next[k];
      clearTimeout(saveTimers.current[k]);
      saveTimers.current[k] = setTimeout(async () => {
        setSaving(k);
        try {
          await api.put('/api/ai/model-rates', {
            provider: m.provider, model: m.model,
            rate_in:  cur.in  === '' ? null : cur.in,
            rate_out: cur.out === '' ? null : cur.out,
          });
        } catch (e) {
          console.warn('[CostForecast] save rate:', e.message);
        } finally { setSaving(s2 => (s2 === k ? '' : s2)); }
      }, 700);
      return next;
    });
  };

  const [seeding, setSeeding] = React.useState(false);
  const doSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/api/ai/model-rates/seed');
      await loadRates();
      window.alert(L.seedDone(res.data?.added || 0, (res.data?.skipped || []).length));
    } catch (e) {
      window.alert('Gagal: ' + (e.response?.data?.error || e.message));
    } finally { setSeeding(false); }
  };
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n >= 0 ? n : 0; };
  const hasRate = (m) => {
    const r = rateOf(m);
    return String(r.in).trim() !== '' || String(r.out).trim() !== '';
  };

  // Biaya per model pada jendela ini. Input dan output dihitung TERPISAH sampai
  // akhir — menggabungkannya lebih dulu akan menghapus justru selisih tarif yang
  // menjadi alasan halaman ini ada.
  const costRows = byModel.map(m => {
    const r = rateOf(m);
    const cIn  = (m.tokens_in  || 0) / 1e6 * num(r.in);
    const cOut = (m.tokens_out || 0) / 1e6 * num(r.out);
    return { ...m, rate_in: r.in, rate_out: r.out, cost_in: cIn, cost_out: cOut, cost: cIn + cOut, priced: hasRate(m) };
  });
  const costIn    = costRows.reduce((a, r) => a + r.cost_in,  0);
  const costOut   = costRows.reduce((a, r) => a + r.cost_out, 0);
  const costTotal = costIn + costOut;
  const tokTotal  = byModel.reduce((a, m) => a + (m.tokens_in || 0) + (m.tokens_out || 0), 0);
  const tokUnpriced = costRows.filter(r => !r.priced)
    .reduce((a, m) => a + (m.tokens_in || 0) + (m.tokens_out || 0), 0);
  const pctUnpriced = tokTotal ? Math.round((tokUnpriced / tokTotal) * 1000) / 10 : 0;

  // ── Proyeksi ───────────────────────────────────────────────────────────────
  // Biaya harian dihitung dari komposisi in/out hari itu memakai tarif RATA-RATA
  // TERTIMBANG jendela — bukan tarif satu model. Instalasi ini memang memakai
  // beberapa model berdampingan, dan memilih salah satunya akan bias.
  const blendIn  = byModel.reduce((a, m) => a + (m.tokens_in  || 0), 0);
  const blendOut = byModel.reduce((a, m) => a + (m.tokens_out || 0), 0);
  const effRateIn  = blendIn  ? (costIn  / blendIn)  * 1e6 : 0;
  const effRateOut = blendOut ? (costOut / blendOut) * 1e6 : 0;
  const dayCost = daily.map(d =>
    (d.tokens_in || 0) / 1e6 * effRateIn + (d.tokens_out || 0) / 1e6 * effRateOut);
  const sorted  = dayCost.slice().sort((a, b) => a - b);
  const dAvg    = sorted.length ? sorted.reduce((a, b) => a + b, 0) / sorted.length : 0;
  const dMedian = sorted.length ? (sorted.length % 2
    ? sorted[Math.floor(sorted.length / 2)]
    : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2) : 0;
  const dMax    = sorted.length ? sorted[sorted.length - 1] : 0;

  const [basis, setBasis] = React.useState('calendar');   // 'calendar' | 'active'
  const activeDays = data?.activeDays || 0;
  const windowDays = data?.windowDays || days;
  // Hari aktif per bulan diskalakan dari proporsi jendela, bukan disamakan
  // dengan jumlah hari aktif mentah — jendela 7 hari dan 90 hari harus
  // menghasilkan proyeksi bulanan yang sebanding.
  const monthDays = basis === 'calendar'
    ? 30
    : Math.max(1, Math.round((activeDays / Math.max(1, windowDays)) * 30));

  // ── Simulator ──────────────────────────────────────────────────────────────
  const [sim, setSim] = React.useState(null);
  const seed = React.useCallback(() => {
    const heaviest = byModel[0];
    const r = heaviest ? (rates[`${heaviest.provider}/${heaviest.model}`] || {}) : {};
    setSim({
      users:   Math.max(1, data?.totals?.users || 1),
      sessPer: 1,
      tokIn:   perSess?.tokens_in?.median  || 0,
      tokOut:  perSess?.tokens_out?.median || 0,
      dayCount: monthDays,
      rateIn:  r.in  ?? (effRateIn  ? effRateIn.toFixed(3)  : ''),
      rateOut: r.out ?? (effRateOut ? effRateOut.toFixed(3) : ''),
    });
  }, [byModel, rates, data, perSess, monthDays, effRateIn, effRateOut]);
  React.useEffect(() => { if (data && !sim) seed(); }, [data, sim, seed]);

  const S = sim || { users: 0, sessPer: 0, tokIn: 0, tokOut: 0, dayCount: 30, rateIn: '', rateOut: '' };
  const simSessions = num(S.users) * num(S.sessPer) * num(S.dayCount);
  const simTokIn    = simSessions * num(S.tokIn);
  const simTokOut   = simSessions * num(S.tokOut);
  const simCost     = simTokIn / 1e6 * num(S.rateIn) + simTokOut / 1e6 * num(S.rateOut);

  const [idr, setIdr] = React.useState('');
  const inIdr = (v) => {
    const k = parseFloat(idr);
    if (!Number.isFinite(k) || k <= 0) return null;
    return 'Rp ' + Math.round(v * k).toLocaleString('id-ID');
  };

  const setS = (k, v) => setSim(prev => ({ ...(prev || S), [k]: v }));

  const doExport = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(costRows.map(r => ({
      Provider: r.provider, Model: r.model, Messages: r.messages, Sessions: r.sessions,
      TokensIn: r.tokens_in, TokensOut: r.tokens_out,
      RateIn: r.rate_in, RateOut: r.rate_out,
      CostIn: r.cost_in, CostOut: r.cost_out, CostTotal: r.cost,
      Priced: r.priced ? 'yes' : 'NO',
    }))), 'By model');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(daily.map((d, i) => ({
      Day: d.day, Messages: d.messages, Sessions: d.sessions, Users: d.users,
      TokensIn: d.tokens_in, TokensOut: d.tokens_out, CostUSD: dayCost[i],
    }))), 'Daily');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byUser.map(u => ({
      User: u.username, Public: u.is_public ? 'yes' : 'no', Sessions: u.sessions,
      Messages: u.messages, TokensIn: u.tokens_in, TokensOut: u.tokens_out,
    }))), 'By user');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { Metric: 'Window days', Value: windowDays },
      { Metric: 'Active days', Value: activeDays },
      { Metric: 'Window cost USD', Value: costTotal },
      { Metric: 'Unpriced tokens', Value: tokUnpriced },
      { Metric: 'Unpriced %', Value: pctUnpriced },
      { Metric: 'Monthly from avg USD', Value: dAvg * monthDays },
      { Metric: 'Monthly from median USD', Value: dMedian * monthDays },
      { Metric: 'Monthly from peak USD', Value: dMax * monthDays },
      { Metric: 'Simulated monthly USD', Value: simCost },
    ]), 'Summary');
    XLSX.writeFile(wb, `ai-cost-forecast-${exportStamp()}.xlsx`);
  };

  const Card = ({ title, lead, children, right }) => (
    <div className="bg-white rounded-xl border border-slate-100 p-4 mb-5">
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</div>
        {right}
      </div>
      {lead && <p className="text-[11px] text-slate-400 leading-relaxed mb-3">{lead}</p>}
      {children}
    </div>
  );

  const NumIn = ({ value, onChange, w = 'w-24', step = 'any' }) => (
    <input type="number" min="0" step={step} value={value}
      onChange={e => onChange(e.target.value)}
      className={`${w} px-2 py-1 text-xs text-right rounded border border-slate-200
                  focus:outline-none focus:ring-2 focus:ring-brand-200`} />
  );

  const Stat = ({ label, value, sub, tone: tn = 'text-slate-800' }) => (
    <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${tn}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-slate-500">{L.range}:</span>
        {RANGES.map(r => (
          <button key={r} onClick={() => setDays(r)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
              days === r ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {L.days(r)}
          </button>
        ))}
        <button onClick={doExport} disabled={!byModel.length}
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                     border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40">
          <Download style={{ width: 12, height: 12 }} />
          {L.export}
        </button>
      </div>

      {loading && (
        <div className="p-8 text-center text-slate-400">
          <Loader2 style={{ width: 20, height: 20 }} className="animate-spin mx-auto mb-2" />
          Loading…
        </div>
      )}

      {!loading && !byModel.length && (
        <div className="p-8 text-center text-slate-400 text-sm bg-white rounded-xl border border-slate-100">
          {L.empty}
        </div>
      )}

      {!loading && byModel.length > 0 && (
        <>
          {/* Peringatan trafik tak berharga — DI ATAS, sebelum angka apa pun.
              Ditaruh di sini dengan sengaja: pembaca yang berhenti di kartu
              pertama tetap melihat batas kepercayaan angkanya. */}
          {tokUnpriced > 0 && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertCircle style={{ width: 15, height: 15 }} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-amber-800">{L.warnTitle}</div>
                  <p className="text-[11px] text-amber-700 leading-relaxed mt-1">
                    {L.warnBody(fmt(tokUnpriced), pctUnpriced)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Tarif per model ── */}
          <Card title={L.ratesTitle} lead={L.ratesLead}
            right={
              <div className="flex items-center gap-2">
                {saving && <span className="text-[11px] text-slate-400">{L.saving}</span>}
                <button onClick={doSeed} disabled={seeding}
                  className="px-2 py-0.5 rounded text-[11px] border border-slate-200 bg-white
                             text-slate-500 hover:bg-slate-50 disabled:opacity-40">
                  {L.seed}
                </button>
              </div>
            }>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-max">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="text-left  font-medium py-1.5 pr-4">{L.colModel}</th>
                    <th className="text-right font-medium py-1.5 px-3">{L.colIn}</th>
                    <th className="text-right font-medium py-1.5 px-3">{L.colOut}</th>
                    <th className="text-right font-medium py-1.5 px-3">{L.colRateIn}</th>
                    <th className="text-right font-medium py-1.5 px-3">{L.colRateOut}</th>
                    <th className="text-right font-medium py-1.5 pl-3">{L.colCost}</th>
                  </tr>
                </thead>
                <tbody>
                  {costRows.map((m, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-1.5 pr-4">
                        <span className="text-slate-700">{m.provider}</span>
                        <span className="text-slate-400"> / {m.model}</span>
                        {!m.priced && (
                          <span className="ml-1.5 text-[10px] text-amber-700 bg-amber-50 px-1 rounded">
                            {L.unpriced}
                          </span>
                        )}
                      </td>
                      <td className="text-right py-1.5 px-3 text-slate-600">{fmt(m.tokens_in)}</td>
                      <td className="text-right py-1.5 px-3 text-slate-600">{fmt(m.tokens_out)}</td>
                      <td className="text-right py-1.5 px-3">
                        <NumIn value={m.rate_in} onChange={v => setRate(m, 'in', v)} w="w-20" />
                      </td>
                      <td className="text-right py-1.5 px-3">
                        <NumIn value={m.rate_out} onChange={v => setRate(m, 'out', v)} w="w-20" />
                      </td>
                      <td className="text-right py-1.5 pl-3 font-medium text-emerald-700">{usd(m.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Biaya jendela ── */}
          <Card title={L.windowTitle} lead={L.windowLead}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Stat label={L.wCost} value={usd(costTotal)} sub={inIdr(costTotal)} tone="text-emerald-700" />
              <Stat label={L.wOutShare}
                value={`${costTotal ? Math.round((costOut / costTotal) * 1000) / 10 : 0}%`}
                sub={`${usd(costOut)} / ${usd(costTotal)}`} tone="text-amber-600" />
              <Stat label={L.wTokens} value={fmt(tokTotal)}
                sub={`${fmt(blendIn)}↑ ${fmt(blendOut)}↓`} />
            </div>
            <p className="text-[11px] text-slate-400 mt-2.5 leading-relaxed">{L.outNote}</p>
          </Card>

          {/* ── Proyeksi bulanan ── */}
          <Card title={L.projTitle} lead={L.projLead}
            right={
              <div className="flex items-center gap-1">
                {[['calendar', L.basisCal], ['active', L.basisActive]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setBasis(k)}
                    className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${
                      basis === k ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-500 border-slate-200'}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            }>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Stat label={L.pMedian} value={usd(dMedian * monthDays) + ' ' + L.perMonth} sub={inIdr(dMedian * monthDays)} />
              <Stat label={L.pAvg}    value={usd(dAvg * monthDays) + ' ' + L.perMonth}    sub={inIdr(dAvg * monthDays)} tone="text-emerald-700" />
              <Stat label={L.pMax}    value={usd(dMax * monthDays) + ' ' + L.perMonth}    sub={inIdr(dMax * monthDays)} tone="text-amber-600" />
            </div>
            <p className="text-[11px] text-slate-400 mt-2.5 leading-relaxed">
              {L.basisNote(activeDays, windowDays)} — {L.basisTitle}: {monthDays}
            </p>
          </Card>

          {/* ── Sebaran per sesi ── */}
          <Card title={L.perSessTitle} lead={L.perSessLead}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label={L.sAvg}    value={fmt(perSess?.total?.avg)} />
              <Stat label={L.sMedian} value={fmt(perSess?.total?.median)} tone="text-emerald-700" />
              <Stat label={L.sP90}    value={fmt(perSess?.total?.p90)} />
              <Stat label={L.sMax}    value={fmt(perSess?.total?.max)} tone="text-amber-600" />
            </div>
          </Card>

          {/* ── Simulator ── */}
          <Card title={L.simTitle} lead={L.simLead}
            right={
              <button onClick={seed}
                className="px-2 py-0.5 rounded text-[11px] border border-slate-200 bg-white text-slate-500 hover:bg-slate-50">
                {L.simSeed}
              </button>
            }>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['users',    L.simUsers,   '1'],
                ['sessPer',  L.simSess,    'any'],
                ['tokIn',    L.simIn,      '1'],
                ['tokOut',   L.simOut,     '1'],
                ['dayCount', L.simDays,    '1'],
                ['rateIn',   L.simRateIn,  'any'],
                ['rateOut',  L.simRateOut, 'any'],
              ].map(([k, label, step]) => (
                <div key={k}>
                  <div className="text-[11px] text-slate-500 mb-1">{label}</div>
                  <NumIn value={S[k]} onChange={v => setS(k, v)} w="w-full" step={step} />
                </div>
              ))}
              <div>
                <div className="text-[11px] text-slate-500 mb-1">{L.idrTitle}</div>
                <NumIn value={idr} onChange={setIdr} w="w-full" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <Stat label={L.simTokens} value={fmt(simTokIn + simTokOut)}
                sub={`${fmt(simTokIn)}↑ ${fmt(simTokOut)}↓ · ${fmt(simSessions)} sesi`} />
              <Stat label={L.simResult} value={usd(simCost) + ' ' + L.perMonth}
                sub={inIdr(simCost)} tone="text-emerald-700" />
            </div>
            <p className="text-[11px] text-slate-400 mt-2.5 leading-relaxed">{L.idrNote}</p>
          </Card>

          {/* ── Per user ── */}
          {byUser.length > 0 && (
            <Card title={L.userTitle} lead={L.userLead}>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs min-w-max">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left  font-medium py-1.5 pr-4">{L.colUser}</th>
                      <th className="text-right font-medium py-1.5 px-3">{L.colSess}</th>
                      <th className="text-right font-medium py-1.5 px-3">{L.colMsg}</th>
                      <th className="text-right font-medium py-1.5 px-3">{L.colIn}</th>
                      <th className="text-right font-medium py-1.5 pl-3">{L.colOut}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUser.map((u, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="py-1.5 pr-4 text-slate-700">
                          {u.username}
                          {u.is_public && (
                            <span className="ml-1.5 text-[10px] text-sky-700 bg-sky-50 px-1 rounded">{L.public}</span>
                          )}
                        </td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(u.sessions)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(u.messages)}</td>
                        <td className="text-right py-1.5 px-3 text-slate-600">{fmt(u.tokens_in)}</td>
                        <td className="text-right py-1.5 pl-3 text-slate-600">{fmt(u.tokens_out)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
